import { Button, HStack } from '@sgme/ui';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import { FxFormattedNumber } from '@/App/tiles/Option/common/FxFormattedNumber.tsx';
import { OptionFormGroup } from '@/App/tiles/Option/fields/OptionFormGroup.tsx';
import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { getQuote, selectOptionLegsProperties } from '@/store/option/selectors/optionSelectors.ts';
import type { LegId } from '@/store/option/state.ts';

type CollapseStatus = 'collapsed' | 'expanded';

type GreeksDataProps = {
  tileId: string;
  onClick: (collapseGreeksStatus: CollapseStatus) => void;
  expanded: boolean;
  canCollapse: boolean;
};

export function GreeksData({ tileId, onClick, expanded, canCollapse }: GreeksDataProps) {
  const legId: LegId = `${tileId}/0`;
  const collapseGreeksStatus = expanded ? 'expanded' : 'collapsed';

  const isExpired = useOptionSelector((option) => option.properties.rfs.status === 'EXPIRED');
  const { greeksData, currency, hedgeType } = useOptionSelector((option) => {
    const legProperties = selectOptionLegsProperties(option, legId);

    const currencyPair = option?.properties.currencyPair;
    const notionalCurrencyIndex = legProperties?.notionalCurrencyIndex;

    const [ccy1, ccy2] = currencyPair?.split('/') ?? [undefined, undefined];
    const currency = notionalCurrencyIndex === 1 ? ccy1 : ccy2;

    const quote = getQuote(option);
    const ccy1GreeksData = quote?.ccy1;
    const ccy2GreeksData = quote?.ccy2;

    return {
      greeksData: notionalCurrencyIndex === 1 ? ccy1GreeksData : ccy2GreeksData,
      currency,
      hedgeType: option.properties.hedgeType,
    };
  });

  const handleCollapseGreeks = () => {
    const newStatus = collapseGreeksStatus === 'collapsed' ? 'expanded' : 'collapsed';
    onClick(newStatus);
  };

  const isPanelOpen = collapseGreeksStatus === 'expanded' || !canCollapse;
  return (
    <>
      {canCollapse && (
        <HStack verticalAlignment="center" fullWidth className="px-2">
          <div className="flex-fill border-top" style={{ height: 1 }} />

          <Button size="sm" purpose="flat" onClick={handleCollapseGreeks} data-e2e="greeks-toggle-button">
            <FormattedMessage id="option.sections.greeks" />
            <i className="icon icon-sm">
              {collapseGreeksStatus === 'collapsed' ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            </i>
          </Button>

          <div className="flex-fill border-top" style={{ height: 1 }} />
        </HStack>
      )}

      {isPanelOpen && (
        <>
          <OptionFormGroup
            label={<FormattedMessage id="option.fields.greeks.delta" values={{ hedged: hedgeType !== 'Live' }} />}
            className={clsx({ expired: isExpired })}
          >
            <FxFormattedNumber data-e2e={'greeks-delta'} value={greeksData?.deltaCcy} currency={currency} />
          </OptionFormGroup>

          <OptionFormGroup
            label={<FormattedMessage id="option.fields.greeks.vega" values={{ hedgeType }} />}
            className={clsx({ expired: isExpired })}
          >
            <FxFormattedNumber data-e2e={'greeks-vega'} value={greeksData?.vegaCcy} currency={currency} />
          </OptionFormGroup>

          <OptionFormGroup
            label={<FormattedMessage id="option.fields.greeks.gamma" />}
            className={clsx({ expired: isExpired })}
          >
            <FxFormattedNumber data-e2e="greeks-gamma" value={greeksData?.gammaCcy} currency={currency} />
          </OptionFormGroup>

          <OptionFormGroup
            label={<FormattedMessage id="option.fields.greeks.theta" />}
            className={clsx({ expired: isExpired })}
          >
            <FxFormattedNumber data-e2e="greeks-theta" value={greeksData?.thetaCcy} currency={currency} />
          </OptionFormGroup>
        </>
      )}
    </>
  );
}
